import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  sortedByZIndex,
  type AutoLayer,
  type Crop,
  type ImageLayer,
  type Layer,
  type LayerTree,
  type ScreenShareLayer,
  type ShapeLayer,
  type TemplateLayer,
  type TextLayer,
  type VideoLayer,
  type FitMode,
} from '@liveforge/shared';
import { ImageCache } from './image-cache';
import type { TrackRegistry } from './track-registry';

/**
 * Draws the layer tree onto a 2D canvas, once per animation frame.
 *
 * The canvas is always 1920x1080 internally, whatever size it is displayed at.
 * Every coordinate in the layer tree is therefore in output pixels and means
 * the same thing in the host's small preview and in Egress's full-size capture
 * - which is the whole point of the two sharing this code.
 */

interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Where a source of the given size lands inside the layer's box. */
function fitBox(fit: FitMode, sourceWidth: number, sourceHeight: number, box: Box): Box {
  if (fit === 'fill' || sourceWidth <= 0 || sourceHeight <= 0) {
    return box;
  }

  const scale =
    fit === 'cover'
      ? Math.max(box.width / sourceWidth, box.height / sourceHeight)
      : Math.min(box.width / sourceWidth, box.height / sourceHeight);

  const width = sourceWidth * scale;
  const height = sourceHeight * scale;
  return {
    x: box.x + (box.width - width) / 2,
    y: box.y + (box.height - height) / 2,
    width,
    height,
  };
}

function roundedRectPath(ctx: CanvasRenderingContext2D, box: Box, radius: number): void {
  const limit = Math.min(radius, box.width / 2, box.height / 2);
  ctx.beginPath();
  if (limit <= 0) {
    ctx.rect(box.x, box.y, box.width, box.height);
  } else {
    ctx.roundRect(box.x, box.y, box.width, box.height, limit);
  }
}

/**
 * How expensive a given mount is allowed to be.
 *
 * Layer coordinates are always in ${CANVAS_WIDTH}x${CANVAS_HEIGHT} space, whatever
 * this says: the renderer scales the whole frame instead, so the same tree
 * composes identically at every size. Egress renders at full size because it
 * produces the broadcast; a preview in a corner of a phone does not have to,
 * and full HD at display refresh rate is enough to make a handset throttle -
 * which would degrade the call itself, not just the picture.
 */
export interface RenderQuality {
  /** Canvas pixel width. */
  width: number;
  /** Canvas pixel height. */
  height: number;
  /** Upper bound on frames per second. 0 draws on every animation frame. */
  maxFps: number;
}

/** What the broadcast is composed at, and the default everywhere. */
export const FULL_QUALITY: RenderQuality = {
  width: CANVAS_WIDTH,
  height: CANVAS_HEIGHT,
  maxFps: 0,
};

/**
 * Where `count` equal tiles land inside a box.
 *
 * Columns come from the square root, which keeps tiles as close to the box's
 * own proportions as a uniform grid can. A final short row is centred rather
 * than left-aligned: an odd participant hanging off one side reads as a
 * mistake, and centring is what every video call has trained people to expect.
 */
function gridBoxes(count: number, box: Box, gap: number): Box[] {
  if (count <= 0) {
    return [];
  }

  const columns = Math.ceil(Math.sqrt(count));
  const rows = Math.ceil(count / columns);
  const tileWidth = (box.width - gap * (columns - 1)) / columns;
  const tileHeight = (box.height - gap * (rows - 1)) / rows;

  const boxes: Box[] = [];
  for (let index = 0; index < count; index += 1) {
    const row = Math.floor(index / columns);
    const column = index - row * columns;
    const inThisRow = Math.min(columns, count - row * columns);
    const rowWidth = inThisRow * tileWidth + (inThisRow - 1) * gap;

    boxes.push({
      x: box.x + (box.width - rowWidth) / 2 + column * (tileWidth + gap),
      y: box.y + row * (tileHeight + gap),
      width: tileWidth,
      height: tileHeight,
    });
  }
  return boxes;
}

export class CompositorRenderer {
  private readonly images: ImageCache;
  private layers: LayerTree = [];
  private frame = 0;
  private ctx: CanvasRenderingContext2D | null = null;
  private running = false;
  private scaleX = 1;
  private scaleY = 1;
  private minFrameMs = 0;
  private placeholders = false;
  /** When the next frame is due, advanced in whole periods rather than from `now`. */
  private nextDrawAt = 0;
  /** The previous animation frame, which is how the loop's own period is known. */
  private lastTick = 0;
  /*
   * Cumulative, never averaged here: a caller samples them twice and divides,
   * which lets it choose its own window without this class holding one.
   */
  private drawnFrames = 0;
  private drawnMs = 0;

  constructor(private readonly tracks: TrackRegistry) {
    // A late-decoding image must trigger a repaint; the loop is running
    // anyway, so this only matters if it is ever made demand-driven.
    this.images = new ImageCache(() => undefined);
  }

  start(canvas: HTMLCanvasElement, quality: RenderQuality = FULL_QUALITY): void {
    this.stop();

    // `alpha: false` lets the compositor skip blending the canvas against the
    // page, and the output is opaque in any case.
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) {
      throw new Error('This browser did not provide a 2D canvas context.');
    }

    this.ctx = ctx;
    this.setQuality(quality);
    this.running = true;
    this.nextDrawAt = 0;
    this.lastTick = 0;
    this.frame = requestAnimationFrame(this.tick);
  }

  /**
   * Resizes and re-paces a running renderer. Deliberately separate from
   * `start`, so changing quality does not tear down the track registry and
   * rebuild a video element per participant.
   */
  setQuality(quality: RenderQuality): void {
    this.scaleX = quality.width / CANVAS_WIDTH;
    this.scaleY = quality.height / CANVAS_HEIGHT;
    this.minFrameMs = quality.maxFps > 0 ? 1000 / quality.maxFps : 0;

    const canvas = this.ctx?.canvas;
    if (canvas && (canvas.width !== quality.width || canvas.height !== quality.height)) {
      canvas.width = quality.width;
      canvas.height = quality.height;
    }
  }

  stop(): void {
    this.running = false;
    if (this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
    this.ctx = null;
  }

  /**
   * Whether a layer with nothing to draw leaves a labelled marker behind.
   *
   * The director needs to see an unassigned slot; the audience must not. Off
   * by default, so every renderer that is an output - a guest, Egress - is
   * silent about the studio's unfinished work without having to say so.
   */
  setPlaceholders(show: boolean): void {
    this.placeholders = show;
  }

  setLayers(layers: LayerTree): void {
    this.layers = layers;
  }

  private readonly tick = (now: number) => {
    if (!this.running) return;

    // Queued before the frame is drawn, so a skipped frame still schedules the
    // next one and a slow draw cannot stall the loop.
    this.frame = requestAnimationFrame(this.tick);

    const loopMs = this.lastTick > 0 ? now - this.lastTick : 0;
    this.lastTick = now;

    if (this.minFrameMs > 0) {
      /*
       * Draw on the tick **nearest** the due time, not on the first tick
       * strictly past it.
       *
       * The loop is quantised - it can only draw when an animation frame
       * fires - so a due time that falls a hair after a tick costs an entire
       * loop period. At 60Hz against a 30fps cap that turns a 33ms interval
       * into 50ms, which is a quarter of the frames lost to arithmetic and
       * nothing else. It is what made the composition draw 22-26fps under a
       * cap of 30 while each frame cost under ten milliseconds.
       *
       * Half the loop's own period is the right tolerance: it draws on
       * whichever tick is closer to when the frame was actually wanted.
       */
      if (now + loopMs / 2 < this.nextDrawAt) return;

      /*
       * Advanced by whole periods, so the overshoot of one frame does not push
       * the next one later - which is the same error compounding. If a stall
       * has left the schedule in the past, it resynchronises rather than
       * drawing a burst to catch up: nobody wants the frames that were missed,
       * they want the next one on time.
       */
      const scheduled = this.nextDrawAt + this.minFrameMs;
      this.nextDrawAt = scheduled > now ? scheduled : now + this.minFrameMs;
    }

    this.draw();
  };

  /**
   * Frames actually drawn, and the time spent drawing them.
   *
   * Counting animation frames instead measures the loop, not the work: with a
   * frame cap the loop still fires at whatever rate the machine allows and the
   * renderer simply returns, so a page that draws thirty and skips thirty
   * looks identical to one drawing sixty. That distinction is the difference
   * between a recording that judders and one that does not.
   */
  counters(): { frames: number; ms: number } {
    return { frames: this.drawnFrames, ms: this.drawnMs };
  }

  private draw(): void {
    const ctx = this.ctx;
    if (!ctx) return;

    const startedAt = performance.now();

    // Re-established every frame rather than once, so nothing a layer does to
    // the transform can leak into the next frame.
    ctx.setTransform(this.scaleX, 0, 0, this.scaleY, 0, 0);

    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    for (const layer of sortedByZIndex(this.layers)) {
      if (!layer.visible || layer.opacity <= 0) {
        continue;
      }
      ctx.save();
      ctx.globalAlpha = Math.min(1, Math.max(0, layer.opacity));

      // Rotation is about the layer's centre, so a rotated layer stays where
      // the host put it instead of swinging away from its own top-left corner.
      if (layer.rotation !== 0) {
        const cx = layer.x + layer.width / 2;
        const cy = layer.y + layer.height / 2;
        ctx.translate(cx, cy);
        ctx.rotate((layer.rotation * Math.PI) / 180);
        ctx.translate(-cx, -cy);
      }

      this.drawLayer(ctx, layer);
      ctx.restore();
    }

    this.drawnFrames += 1;
    this.drawnMs += performance.now() - startedAt;
  }

  private drawLayer(ctx: CanvasRenderingContext2D, layer: Layer): void {
    switch (layer.type) {
      case 'video':
      case 'screenshare':
        this.drawVideo(ctx, layer);
        return;
      case 'auto':
        this.drawAuto(ctx, layer);
        return;
      case 'image':
        this.drawImage(ctx, layer);
        return;
      case 'text':
        this.drawText(ctx, layer);
        return;
      case 'shape':
        this.drawShape(ctx, layer);
        return;
      case 'template':
        if (this.placeholders) {
          this.drawTemplatePlaceholder(ctx, layer);
        }
        return;
    }
  }

  private drawVideo(ctx: CanvasRenderingContext2D, layer: VideoLayer | ScreenShareLayer): void {
    const box: Box = { x: layer.x, y: layer.y, width: layer.width, height: layer.height };
    this.paintVideo(ctx, this.tracks.resolve(layer), box, layer.fit, {
      crop: layer.crop,
      mirrored: layer.type === 'video' && layer.mirrored,
      cornerRadius: 0,
    });
  }

  /**
   * Every participant present, arranged inside the layer's box.
   *
   * The tiles come from the registry rather than from the layer, which names
   * nobody - so this is the one thing on screen that can change without the
   * host sending anything. Somebody joining appears here, on air, at once.
   */
  private drawAuto(ctx: CanvasRenderingContext2D, layer: AutoLayer): void {
    const box: Box = { x: layer.x, y: layer.y, width: layer.width, height: layer.height };
    const tiles = this.tracks.tiles();

    if (tiles.length === 0) {
      if (this.placeholders) {
        this.drawEmptySlot(ctx, box, 'No participants yet');
      }
      return;
    }

    const boxes = gridBoxes(tiles.length, box, layer.gap);
    boxes.forEach((tileBox, index) => {
      const entry = tiles[index];
      if (!entry) return;
      this.paintVideo(ctx, entry.element, tileBox, layer.fit, {
        crop: null,
        mirrored: false,
        cornerRadius: layer.cornerRadius,
      });
    });
  }

  /** The one place a video frame is actually put on the canvas. */
  private paintVideo(
    ctx: CanvasRenderingContext2D,
    element: HTMLVideoElement | undefined,
    box: Box,
    fit: FitMode,
    options: { crop: Crop | null; mirrored: boolean; cornerRadius: number },
  ): void {
    if (!element || element.readyState < 2 || element.videoWidth === 0) {
      if (this.placeholders) {
        this.drawEmptySlot(ctx, box, element ? 'No signal' : 'No source assigned');
      }
      return;
    }

    let sourceX = 0;
    let sourceY = 0;
    let sourceWidth = element.videoWidth;
    let sourceHeight = element.videoHeight;

    if (options.crop) {
      sourceX = options.crop.x * element.videoWidth;
      sourceY = options.crop.y * element.videoHeight;
      sourceWidth = options.crop.width * element.videoWidth;
      sourceHeight = options.crop.height * element.videoHeight;
    }

    const target = fitBox(fit, sourceWidth, sourceHeight, box);

    // Clipping matters for `cover`, where the fitted image is deliberately
    // larger than the box and would otherwise spill over its neighbours.
    ctx.save();
    if (options.cornerRadius > 0) {
      roundedRectPath(ctx, box, options.cornerRadius);
    } else {
      ctx.beginPath();
      ctx.rect(box.x, box.y, box.width, box.height);
    }
    ctx.clip();

    if (options.mirrored) {
      ctx.translate(box.x + box.width / 2, 0);
      ctx.scale(-1, 1);
      ctx.translate(-(box.x + box.width / 2), 0);
    }

    ctx.drawImage(
      element,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      target.x,
      target.y,
      target.width,
      target.height,
    );
    ctx.restore();
  }

  private drawImage(ctx: CanvasRenderingContext2D, layer: ImageLayer): void {
    const box: Box = { x: layer.x, y: layer.y, width: layer.width, height: layer.height };
    const image = this.images.get(layer.url);
    if (!image) {
      this.drawEmptySlot(ctx, box, layer.url ? 'Loading image' : 'No image URL');
      return;
    }

    const target = fitBox(layer.fit, image.naturalWidth, image.naturalHeight, box);
    ctx.save();
    ctx.beginPath();
    ctx.rect(box.x, box.y, box.width, box.height);
    ctx.clip();
    ctx.drawImage(image, target.x, target.y, target.width, target.height);
    ctx.restore();
  }

  private drawText(ctx: CanvasRenderingContext2D, layer: TextLayer): void {
    ctx.font = `${layer.fontWeight} ${layer.fontSize}px ${layer.fontFamily}`;
    ctx.textBaseline = 'top';
    ctx.textAlign = layer.align;
    ctx.fillStyle = layer.color;

    const originX =
      layer.align === 'center'
        ? layer.x + layer.width / 2
        : layer.align === 'right'
          ? layer.x + layer.width
          : layer.x;

    const lineHeight = layer.fontSize * layer.lineHeight;
    // Explicit newlines only: wrapping to the box would need measurement per
    // frame, and a host who wants two lines can type two lines.
    const lines = layer.text.split('\n');

    ctx.save();
    ctx.beginPath();
    ctx.rect(layer.x, layer.y, layer.width, layer.height);
    ctx.clip();

    lines.forEach((line, position) => {
      const y = layer.y + position * lineHeight;
      if (layer.strokeColor && layer.strokeWidth > 0) {
        ctx.lineWidth = layer.strokeWidth;
        ctx.strokeStyle = layer.strokeColor;
        ctx.strokeText(line, originX, y);
      }
      ctx.fillText(line, originX, y);
    });
    ctx.restore();
  }

  private drawShape(ctx: CanvasRenderingContext2D, layer: ShapeLayer): void {
    const box: Box = { x: layer.x, y: layer.y, width: layer.width, height: layer.height };

    if (layer.shape === 'rect') {
      roundedRectPath(ctx, box, layer.cornerRadius);
    } else {
      const radiusX = box.width / 2;
      // A circle takes the smaller half-extent, so it stays a circle in a box
      // that is not square.
      const radiusY = layer.shape === 'circle' ? Math.min(radiusX, box.height / 2) : box.height / 2;
      ctx.beginPath();
      ctx.ellipse(
        box.x + box.width / 2,
        box.y + box.height / 2,
        layer.shape === 'circle' ? radiusY : radiusX,
        radiusY,
        0,
        0,
        Math.PI * 2,
      );
    }

    if (layer.fill) {
      ctx.fillStyle = layer.fill;
      ctx.fill();
    }
    if (layer.strokeColor && layer.strokeWidth > 0) {
      ctx.lineWidth = layer.strokeWidth;
      ctx.strokeStyle = layer.strokeColor;
      ctx.stroke();
    }
  }

  /**
   * Phase 7 renders these for real. Until then a host who adds one sees a
   * labelled box rather than nothing at all, which would be indistinguishable
   * from a broken layer.
   */
  private drawTemplatePlaceholder(ctx: CanvasRenderingContext2D, layer: TemplateLayer): void {
    this.drawEmptySlot(
      ctx,
      { x: layer.x, y: layer.y, width: layer.width, height: layer.height },
      layer.templateId ? `Template ${layer.templateId} (Phase 7)` : 'Template (Phase 7)',
    );
  }

  private drawEmptySlot(ctx: CanvasRenderingContext2D, box: Box, label: string): void {
    ctx.save();
    ctx.fillStyle = 'rgba(255, 255, 255, 0.06)';
    ctx.fillRect(box.x, box.y, box.width, box.height);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
    ctx.lineWidth = 2;
    ctx.setLineDash([12, 8]);
    ctx.strokeRect(box.x, box.y, box.width, box.height);
    ctx.setLineDash([]);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
    ctx.font = '400 28px Inter, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, box.x + box.width / 2, box.y + box.height / 2);
    ctx.restore();
  }
}
